import Map, { MapRef, Marker, MarkerDragEvent } from "react-map-gl";
import { styled } from "styled-system/jsx";
import { RefObject, useState, Ref, useEffect, useRef } from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import { Button } from "@ttc3k/trekker";
import { RefreshDouble } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { JsxStyleProps } from "styled-system/types";

interface MapboxMapProps extends JsxStyleProps {
  mapCoordinates: { latitude: number; longitude: number };
  markerLng: number;
  markerLat: number;
  markerDraggable?: boolean;
  markerOnDragEnd?: (e: MarkerDragEvent) => void;
  mapRef?: Ref<MapRef>;
  removeResetViewToMarkerButton?: boolean;
}

const StyledMapContainer = styled("div", {
  base: {
    width: "full",
    height: 400,
    borderRadius: "100",
    border: "1px solid token(colors.border.light)",
    overflow: "hidden",
    position: "relative",
  },
});

const StyledRestButton = styled(Button, {
  base: {
    position: "absolute",
    top: "300",
    right: "300",
    zIndex: 10,
    boxShadow: "lg",
  },
});

const isValidLngLat = (lng: unknown, lat: unknown) => {
  const parsedLng = Number(lng);
  const parsedLat = Number(lat);
  return (
    Number.isFinite(parsedLng) &&
    Number.isFinite(parsedLat) &&
    parsedLng >= -180 &&
    parsedLng <= 180 &&
    parsedLat >= -90 &&
    parsedLat <= 90
  );
};

export const MapboxMap = ({
  mapRef,
  mapCoordinates,
  markerLng,
  markerLat,
  markerDraggable = false,
  markerOnDragEnd,
  removeResetViewToMarkerButton = false,
  ...restStyleProps
}: MapboxMapProps) => {
  const { t } = useTranslation("core");
  const [currentViewCoordinates, setCurrentViewCoordinates] =
    useState(mapCoordinates);
  const hasValidMarker = isValidLngLat(markerLng, markerLat);

  const isViewingMarker =
    hasValidMarker &&
    currentViewCoordinates.latitude.toFixed(2) === markerLat.toFixed(2) &&
    currentViewCoordinates.longitude.toFixed(2) === markerLng.toFixed(2);

  /**
   * Handler that gets called when a user moves away from the marker
   * This function will reset the map view to center the marker's coordinates
   */
  const handleResetViewToMarker = () => {
    if (!hasValidMarker) return;

    const refObject = mapRef as RefObject<MapRef>;
    refObject?.current?.flyTo({
      center: { lat: markerLat, lng: markerLng },
      duration: 1500,
    });
  };

  // If the map canvas is initialized off-screen, the canvas will not be the correct size
  // This will resize the canvas when the component becomes visible to ensure the map is
  // displayed correctly
  const mapContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const map = (mapRef as RefObject<MapRef>)?.current;
        if (entry.isIntersecting && map) {
          map.resize();
        }
      },
      { threshold: 0.1 },
    );

    if (mapContainerRef.current) {
      observer.observe(mapContainerRef.current);
    }

    return () => observer.disconnect();
  }, [mapRef]);

  return (
    <StyledMapContainer ref={mapContainerRef} {...restStyleProps}>
      {hasValidMarker &&
        (markerLat !== 0 || markerLng !== 0) &&
        !isViewingMarker &&
        !removeResetViewToMarkerButton && (
          <StyledRestButton
            type={"button"}
            Icon={RefreshDouble}
            mode={"light"}
            size={"lg"}
            onClick={handleResetViewToMarker}
          >
            {t("RECENTER")}
          </StyledRestButton>
        )}
      <Map
        ref={mapRef}
        mapboxAccessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
        initialViewState={{
          ...mapCoordinates,
          zoom: 14,
          pitch: 42.5,
        }}
        mapStyle={import.meta.env.VITE_MAPBOX_STYLESHEET}
        onMove={e =>
          setCurrentViewCoordinates({
            latitude: e.viewState.latitude,
            longitude: e.viewState.longitude,
          })
        }
      >
        {hasValidMarker && (
          <Marker
            longitude={markerLng}
            latitude={markerLat}
            anchor="bottom"
            draggable={markerDraggable}
            onDragEnd={markerOnDragEnd}
          >
            <styled.img
              src={"/icons/map-pin.svg"}
              alt={""}
              w={"24px"}
              h={"24px"}
            />
          </Marker>
        )}
      </Map>
    </StyledMapContainer>
  );
};
